import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidUrl } from "../../utils/validation";
import { message } from "antd";
import { BannerService } from "../../services/bannerService";
import { BannerDetail, BannerFormData, BannerListItem, BannerOrderItem, defaultBannerFormData, OperationStatus } from "./constants";
import { useMemo, useCallback } from "react";
import { getErrorMessage } from "../../utils/errorUtils";


interface BannerContextState {
	// Core Domain Data
	bannerList: BannerListItem[];
	bannerDetail: BannerDetail | null;
	bannerForm: BannerFormData;
	operationStatus: OperationStatus;

	// Setters
	setBannerForm: React.Dispatch<React.SetStateAction<BannerFormData>>;
	clearBannerFormData: () => void;
    clearBannerList: () => void;

	// Actions
	addBanner: () => Promise<void>;
	getBannerList: () => Promise<void>;
	updateBanner: () => Promise<void>;
    removeBanner: (id: string) => Promise<void>;
	getBannerDetails: (id: string) => Promise<void>;
    updateBannerOrders: (orderedList: BannerOrderItem[]) => Promise<void>;
}

interface BannerProviderProps {
    children: React.ReactNode;
}

const BannerContext = createContext<BannerContextState | undefined>(
	undefined
);

export const useBannerContext = () => {
	const context = useContext(BannerContext);
	if (!context) {
		throw new Error(
			"useBannerContext must be used within a BannerContextProvider"
		);
	}
	return context;
};

export const BannerContextProvider: React.FC<BannerProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [bannerList, setBannerList] = useState<BannerListItem[]>([]);
	const [bannerDetail, setBannerDetail] = useState<BannerDetail | null>(null);
	const [bannerForm, setBannerForm] = useState<BannerFormData>(defaultBannerFormData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const isBannerDataValid = (data: BannerFormData) => {
		if (!data.bannerLocalizable || data.bannerLocalizable.length === 0) {
			return false;
		}

		for (const localizable of data.bannerLocalizable) {
			if (!localizable.firstHeader || !localizable.header || !localizable.culture) {
				return false;
			}
			if (data.hasNavigation && !localizable.url) {
				return false;
			}

		}
		return true;
	};

	const hasAllImages = (images: BannerFormData["images"]) => {
		return Object.values(images).every(image => image !== null);
	};

	const hasAnyImage = (images: BannerFormData["images"]) => {
		return Object.values(images).some(image => image !== null);
	};

	const hasAnyLocalizable = (localizables: BannerFormData["bannerLocalizable"]) => {
		return localizables.some(localizable => {
			return [localizable.url, localizable.header, localizable.firstHeader]
				.some(value => typeof value === "string" && value.trim().length > 0);
		});
	};

	const clearBannerFormData = () => {
		setBannerForm(defaultBannerFormData);
		setBannerDetail(null);
	};

	const clearBannerList = () => {
		setBannerList([]);
	};

	const addBanner = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isBannerDataValid(bannerForm) || !hasAllImages(bannerForm.images) || bannerForm.isActive === undefined) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		for (const localizable of bannerForm.bannerLocalizable) {
			if (localizable.url && !isValidUrl(localizable.url)) {
				message.info("Lütfen geçerli bir URL girin.");
				setOperationStatus({ type: "idle" });
				return;
			}
		}

		try {
			const response = await BannerService.addBanner(bannerForm);

			if (response.isSuccessful) {
				message.success("Banner başarıyla eklendi.");
				setBannerForm(defaultBannerFormData);
				navigate("/banners");
			} else {
				message.error(response.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [bannerForm, navigate]);

	const getBannerDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerService.getBannerDetails(id);

			if (response.isSuccessful) {
				setBannerDetail({
					bannerId: response.result.bannerId,
					isActive: response.result.isActive,
					displayOrder: response.result.displayOrder,
					bannerLocalizable: response.result.bannerLocalizable,
					hasNavigation: response.result.hasNavigation,
					imageList: {
						imageUrl: response.result.imageUrl,
						smallImageUrl: response.result.smallImageUrl,
					}
				});

				setBannerForm({
					...defaultBannerFormData,
					bannerId: response.result.bannerId,
					bannerLocalizable: defaultBannerFormData.bannerLocalizable.map(localizable => {
						const matched = response.result.bannerLocalizable.find(item => item.culture === localizable.culture);

						if (!matched) {
							return localizable;
						}

						return {
							...localizable,
							bannerLocalizableId: matched.bannerLocalizableId,
							culture: matched.culture,
						};
					}),
				});
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const updateBanner = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!validateBannerId()) {
			return;
		}

		const { isActivated, hasNavigationChanged } = hasBannerChanges();

		const hasChanges =
			hasAnyLocalizable(bannerForm.bannerLocalizable) ||
			hasAnyImage(bannerForm.images) ||
			isActivated ||
			hasNavigationChanged;

		if (!hasChanges) {
			message.info("Değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const requiresAllUrls = hasNavigationChanged && bannerForm.hasNavigation === true;

		if (!validateBannerUrls(requiresAllUrls)) {
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await BannerService.updateBanner(bannerForm);

			if (response.isSuccessful) {
				message.success("Banner başarıyla güncellendi.");
				setBannerForm(defaultBannerFormData);
				setBannerDetail(null);
				navigate("/banners");
			} else {
				message.error(response.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [bannerForm, navigate]);

	const getBannerList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerService.getBannerList();

			if (response.isSuccessful) {
				setBannerList(response.result?.items || []);
				clearBannerFormData();
			} else {
				message.error(response.error ? response.error : "Banner listesi yüklenirken hata oluştu.");
				setBannerList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [clearBannerFormData]);

	const removeBanner = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await BannerService.removeBanner(id);
			if (response.isSuccessful) {
				message.success("Banner başarıyla silindi.");
				await getBannerList();
			} else {
				message.error(response.error?.message || "Banner silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [getBannerList]);

	const updateBannerOrders = useCallback(async (orderedList: BannerOrderItem[]) => {
		setOperationStatus({ type: "loading", operation: "update" });

		const newBannerList = orderedList.map((item, index) => ({
			bannerId: item.bannerId,
			displayOrder: index + 1,
		}));

		const hasChanges = newBannerList.some((newItem) => {
			const oldItem = bannerList.find((b) => b.bannerId === newItem.bannerId);
			return oldItem?.displayOrder !== newItem.displayOrder;
		});

		if (!hasChanges) {
			setOperationStatus({ type: "idle" });
			message.info("Hiçbir değişiklik bulunmamaktadır.");
			return;
		}

		try {
			const response = await BannerService.updateBannerOrders(newBannerList);
			if (response.isSuccessful) {
				message.success("Banner sıralaması güncellendi.");
				navigate("/banners");
			} else {
				message.error(response.error ? response.error.message : "Banner sıralamaları güncellenirken bir hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [bannerList, navigate]);

	const validateBannerId = () => {
		if (!bannerForm.bannerId) {
			message.info("Banner numarası bulunamadı.");
			return false;
		}

		return true;
	};

	const hasBannerChanges = () => {
		const isActivated =
			bannerForm.isActive !== undefined &&
			bannerForm.isActive !== bannerDetail?.isActive;

		const hasNavigationChanged =
			bannerForm.hasNavigation !== undefined &&
			bannerForm.hasNavigation !== bannerDetail?.hasNavigation;

		return {
			isActivated,
			hasNavigationChanged,
		};
	};

	const validateBannerUrls = (requiresAllUrls: boolean) => {
		for (const localizable of bannerForm.bannerLocalizable) {
			const url = localizable.url?.trim() ?? "";

			if (requiresAllUrls && !url) {
				message.info("Lütfen tüm alanları doldurun");
				return false;
			}

			if (url && !isValidUrl(url)) {
				message.info("Lütfen geçerli bir URL girin.");
				return false;
			}
		}

		return true;
	};


	const contextValue = useMemo<BannerContextState>(
		() => ({
			bannerForm,
			bannerList,
			bannerDetail,
			setBannerForm,
			operationStatus,
			addBanner,
			removeBanner,
			updateBannerOrders,
			getBannerList,
			getBannerDetails,
			clearBannerFormData,
			clearBannerList,
			updateBanner,
		}),
		[
			bannerForm,
			bannerList,
			bannerDetail,
			setBannerForm,
			operationStatus,
			addBanner,
			removeBanner,
			updateBannerOrders,
			getBannerList,
			getBannerDetails,
			clearBannerFormData,
			clearBannerList,
			updateBanner,
		]
	);

	return (
		<BannerContext.Provider value={contextValue}>
			{children}
		</BannerContext.Provider>
	);
};
